/**
 * SessionsList Component
 * 
 * Displays active user sessions with device information and allows
 * termination of individual sessions.
 * 
 * Requirements: 8.3, 8.4
 */

import React, { useState } from 'react';
import type { Session } from '../types/auth.types';
import { ErrorAlert } from './ErrorAlert';
import { LoadingSpinner } from './LoadingSpinner';

export interface SessionsListProps {
  sessions: Session[];
  currentSessionId?: string;
  onTerminateSession: (sessionId: string) => Promise<void>;
  onRefresh?: () => Promise<void>;
}

export const SessionsList: React.FC<SessionsListProps> = ({
  sessions,
  currentSessionId,
  onTerminateSession,
  onRefresh,
}) => {
  const [terminatingSessionId, setTerminatingSessionId] = useState<string | null>(null);
  const [errors, setErrors] = useState<string[]>([]);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const handleTerminate = async (sessionId: string) => {
    setTerminatingSessionId(sessionId);
    setErrors([]);

    try {
      await onTerminateSession(sessionId);
      // Refresh the list after termination
      if (onRefresh) {
        await onRefresh();
      }
    } catch (error) {
      setErrors([error instanceof Error ? error.message : 'Failed to terminate session']);
    } finally {
      setTerminatingSessionId(null);
    }
  };

  const handleRefresh = async () => {
    if (!onRefresh) return;

    setIsRefreshing(true);
    setErrors([]);

    try {
      await onRefresh();
    } catch (error) {
      setErrors([error instanceof Error ? error.message : 'Failed to refresh sessions']);
    } finally {
      setIsRefreshing(false);
    }
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    
    return date.toLocaleDateString();
  };

  const getDeviceIcon = (device: string): string => {
    const deviceLower = device.toLowerCase();
    if (deviceLower.includes('mobile') || deviceLower.includes('phone')) return '📱';
    if (deviceLower.includes('tablet') || deviceLower.includes('ipad')) return '📱';
    return '💻';
  };

  const sessionCardStyle: React.CSSProperties = {
    border: '1px solid #ddd',
    borderRadius: '8px',
    padding: '16px',
    marginBottom: '12px',
    backgroundColor: '#fff',
  };

  const currentSessionStyle: React.CSSProperties = {
    ...sessionCardStyle,
    borderColor: '#3498db',
    backgroundColor: '#f0f8ff',
  };

  const buttonStyle: React.CSSProperties = {
    padding: '6px 12px',
    backgroundColor: '#e74c3c',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    fontSize: '12px',
    fontWeight: 600,
    cursor: 'pointer',
  };

  const refreshButtonStyle: React.CSSProperties = {
    padding: '8px 16px',
    backgroundColor: '#3498db',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    fontSize: '14px',
    fontWeight: 600,
    cursor: isRefreshing ? 'not-allowed' : 'pointer',
    opacity: isRefreshing ? 0.7 : 1,
    marginBottom: '16px',
  };

  if (sessions.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '32px', color: '#666' }}>
        <p>No active sessions found</p>
        {onRefresh && (
          <button onClick={handleRefresh} disabled={isRefreshing} style={refreshButtonStyle}>
            {isRefreshing ? <LoadingSpinner size="small" /> : 'Refresh'}
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="sessions-list">
      {errors.length > 0 && <ErrorAlert message={errors} onDismiss={() => setErrors([])} />}

      {onRefresh && (
        <button onClick={handleRefresh} disabled={isRefreshing} style={refreshButtonStyle}>
          {isRefreshing ? <LoadingSpinner size="small" /> : 'Refresh Sessions'}
        </button>
      )}

      <div style={{ marginBottom: '8px', fontSize: '14px', color: '#666' }}>
        {sessions.length} active session{sessions.length !== 1 ? 's' : ''}
      </div>

      {sessions.map((session) => {
        const isCurrentSession = session.sessionId === currentSessionId;
        const isTerminating = terminatingSessionId === session.sessionId;

        return (
          <div
            key={session.sessionId}
            style={isCurrentSession ? currentSessionStyle : sessionCardStyle}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                  <span style={{ fontSize: '24px' }}>
                    {getDeviceIcon(session.deviceInfo.device)}
                  </span>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: '14px' }}>
                      {session.deviceInfo.browser} on {session.deviceInfo.os}
                    </div>
                    {isCurrentSession && (
                      <span
                        style={{
                          fontSize: '12px',
                          color: '#3498db',
                          fontWeight: 600,
                        }}
                      >
                        Current Session
                      </span>
                    )}
                  </div>
                </div>

                <div style={{ fontSize: '13px', color: '#666', marginBottom: '4px' }}>
                  <strong>IP Address:</strong> {session.ipAddress}
                </div>

                <div style={{ fontSize: '13px', color: '#666', marginBottom: '4px' }}>
                  <strong>Last Activity:</strong> {formatDate(session.lastActivity)}
                </div>

                <div style={{ fontSize: '13px', color: '#666' }}>
                  <strong>Created:</strong> {formatDate(session.createdAt)}
                </div>
              </div>

              <div>
                <button
                  onClick={() => handleTerminate(session.sessionId)}
                  disabled={isTerminating}
                  style={{
                    ...buttonStyle,
                    cursor: isTerminating ? 'not-allowed' : 'pointer',
                    opacity: isTerminating ? 0.7 : 1,
                  }}
                  aria-label={`Terminate session from ${session.deviceInfo.browser}`}
                >
                  {isTerminating ? <LoadingSpinner size="small" /> : 'Terminate'}
                </button>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};
